import {
  addPlaylistVideoBody,
  authPlaylistPath,
  authPlaylistVideoPath,
  authPlaylistVideosPath,
  AUTH_PLAYLISTS_PATH,
  createPlaylistBody,
  err,
  ok,
  parseAddedPlaylistEntry,
  parseCreatedPlaylist,
  parseOwnedPlaylists,
  parsePlaylist,
  playlistPath,
  playlistWireParams,
  resolveInstanceUrl,
  updatePlaylistBody,
  withQuery,
  type AbortLike,
  type AuthScope,
  type CreatedPlaylist,
  type CreatePlaylistInput,
  type ParsedList,
  type Playlist,
  type PlaylistEntry,
  type Result,
  type UpdatePlaylistInput,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { authGet, authGetUrl, authWrite, type AuthApiProblem } from './auth';
import { fetchJson } from './json';
import type { ApiProblem } from './videos';

/**
 * §8's Playlists row, viewing half — one endpoint, and the request plumbing for it.
 *
 * Every paging decision is in `playlist/entries.ts`: which page number is safe to send (below 1 the
 * API **empties** the list at `200`), and when to stop (the last page contributed no new `index`).
 * This file is the plumbing and nothing else.
 *
 * There is no separate "details" request the way a channel has: the endpoint re-sends the whole
 * header — title, author, description, `videoCount`, `updated` — with **every** page, at about 1.5 KB.
 * So a page *is* a `Playlist`, the caller reads the header off the first one, and there is nothing to
 * fetch twice.
 */
export type PlaylistPageResult = Result<Playlist, ApiProblem>;

export async function fetchPlaylistPage(
  platform: Platform,
  playlistId: string,
  page: number,
  signal?: AbortLike,
): Promise<PlaylistPageResult> {
  const path = playlistPath(playlistId);
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, playlistWireParams(page));
  if (url === null) return err({ kind: 'bad-url', path });

  // The zero-byte `200` is reachable rather than theoretical here: `/api/v1/playlists/` with an empty
  // id answers exactly that — the captions trap in a fourth place. `parseRoute` refuses an empty
  // `list`, so this is the second line of defence rather than the first. 3c wrote the check out here;
  // 4a moved it into `api/json.ts` so the endpoints that lacked it have it too.
  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parsePlaylist(body.value, platform.parseContext);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

/* ------------------------------------------------------------------------------------------------
 * The authenticated read, and the five mutations
 * ---------------------------------------------------------------------------------------------- */

export type AuthPlaylistResult = Result<Playlist, AuthApiProblem>;

/**
 * One page of a playlist through `GET /api/v1/auth/playlists/{id}` — the only way to read a **private**
 * one, since the public endpoint answers `404` for it and ignores the credential entirely
 * (`authPlaylistReadPreferred` has the measurement and the rule for choosing).
 *
 * The paging is the *same* as the public endpoint's, which was verified rather than assumed: on a
 * 120-entry native playlist both answered byte-identically — page 1 = index 0..99 (100 rows), page 2 =
 * index 50..119 (70 rows), page 3 empty. So the pages **overlap by 50** and `foldPlaylistPages` is
 * needed here too, with no change: its rule ("the last page added no new `index`") terminates a stride
 * of 50 exactly as it does the mirrored endpoint's ~100.
 */
export async function fetchAuthPlaylistPage(
  platform: Platform,
  playlistId: string,
  page: number,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<AuthPlaylistResult> {
  const path = authPlaylistPath(playlistId);
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  // The same clamp as the public read, and for the same measured reason: `page=0` and `page=-1` answer
  // `200` with `videos: []` and a complete header, so an unclamped number renders a full playlist as
  // an empty one.
  const url = withQuery(base, playlistWireParams(page));
  if (url === null) return err({ kind: 'bad-url', path });

  const raw = await authGetUrl(platform, path, url, grantedScopes, signal);
  if (!raw.ok) return err(raw.error);

  const parsed = parsePlaylist(raw.value, platform.parseContext);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

/**
 * Every playlist the reader owns, entries embedded — see `OWNED_PLAYLIST_LIST_EMBEDS_ENTRIES` for what
 * that costs and why no caller may fetch it eagerly. There is no summary form, no paging and
 * `?fields=` is ignored, all three measured.
 */
export async function fetchOwnedPlaylists(
  platform: Platform,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<ParsedList<Playlist>, AuthApiProblem>> {
  const raw = await authGet(platform, AUTH_PLAYLISTS_PATH, grantedScopes, signal);
  if (!raw.ok) return err(raw.error);
  return ok(parseOwnedPlaylists(raw.value, platform.parseContext));
}

/** `201` + `{title, playlistId}`. The `Location` header is unreadable from a browser — see core. */
export async function createPlaylist(
  platform: Platform,
  input: CreatePlaylistInput,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<CreatedPlaylist, AuthApiProblem>> {
  const raw = await authWrite(
    platform,
    'POST',
    AUTH_PLAYLISTS_PATH,
    createPlaylistBody(input),
    grantedScopes,
    signal,
  );
  if (!raw.ok) return err(raw.error);

  const parsed = parseCreatedPlaylist(raw.value);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

/**
 * `204`, no body. **`title` is mandatory on every call** even when the change is a description or a
 * privacy setting — omitting it is a `403` with a leaked Crystal `KeyError` (`updatePlaylistBody`).
 */
export async function updatePlaylist(
  platform: Platform,
  playlistId: string,
  input: UpdatePlaylistInput,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<null, AuthApiProblem>> {
  const raw = await authWrite(
    platform,
    'PATCH',
    authPlaylistPath(playlistId),
    updatePlaylistBody(input),
    grantedScopes,
    signal,
  );
  return raw.ok ? ok(null) : err(raw.error);
}

/** `204`. A YouTube playlist id and a deleted one answer the same `404 Playlist does not exist.` */
export async function deletePlaylist(
  platform: Platform,
  playlistId: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<null, AuthApiProblem>> {
  const raw = await authWrite(platform, 'DELETE', authPlaylistPath(playlistId), null, grantedScopes, signal);
  return raw.ok ? ok(null) : err(raw.error);
}

/**
 * `201` returning **the whole entry row**, so the caller learns the new `index` and `indexId` without a
 * follow-up read. Two `404`s are reachable and only the body separates them — `Video unavailable` and
 * `Playlist does not exist.` — which is what `notFoundSubject` reads.
 */
export async function addPlaylistVideo(
  platform: Platform,
  playlistId: string,
  videoId: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<PlaylistEntry, AuthApiProblem>> {
  const raw = await authWrite(
    platform,
    'POST',
    authPlaylistVideosPath(playlistId),
    addPlaylistVideoBody(videoId),
    grantedScopes,
    signal,
  );
  if (!raw.ok) return err(raw.error);

  const parsed = parseAddedPlaylistEntry(raw.value, platform.parseContext);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

/**
 * `204`. Keyed on `indexId` **verbatim** — the numeric `index` answers a 404 whose message reads as
 * "that position is gone", a video id answers `403 Invalid Int64`, and a lowercased `indexId` answers
 * the same 404 as a stale one (`authPlaylistVideoPath`).
 */
export async function removePlaylistVideo(
  platform: Platform,
  playlistId: string,
  indexId: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<null, AuthApiProblem>> {
  const raw = await authWrite(
    platform,
    'DELETE',
    authPlaylistVideoPath(playlistId, indexId),
    null,
    grantedScopes,
    signal,
  );
  return raw.ok ? ok(null) : err(raw.error);
}
